import React, { useState, useEffect, useRef, useMemo } from 'react';
import './BottomNavZdo.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

const buildBottomItems = (menuItems = []) => {
  const PINNED_IDS  = ['orders'];
  const MAX_REGULAR = 3;

  const pinned      = menuItems.filter(i => PINNED_IDS.includes(i.id));
  const regular     = menuItems.filter(i => !PINNED_IDS.includes(i.id));
  const regularSlots = MAX_REGULAR - pinned.length;
  const barItems    = [...regular.slice(0, regularSlots), ...pinned];
  const drawerItems = menuItems.filter(i => !barItems.find(b => b.id === i.id));

  return { barItems, drawerItems };
};

// ─── Component ──────────────────────────────────────────────────────────────

const BottomNavZdo = ({
  menuItems       = [],
  level2Items     = {},
  additionalLinks = [],
  activeItem,
  badges          = {},
  onMenuItemClick,
  onAddGroup,
  title           = 'Додати групу',
  rightPossible   = {},
}) => {
  // drawerOpen  — повний drawer (кнопка "Меню")
  // subSheetId  — id bar-пункту чиє підменю показується в окремому міні-sheet
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [subSheetId, setSubSheetId] = useState(null);
  const [expandedInDrawer, setExpandedInDrawer] = useState(null);

  const drawerRef   = useRef(null);
  const subSheetRef = useRef(null);

  const { barItems, drawerItems } = useMemo(
    () => buildBottomItems(menuItems),
    [menuItems]
  );

  // Закрити все при зміні активного пункту
  useEffect(() => {
    setDrawerOpen(false);
    setSubSheetId(null);
  }, [activeItem]);

  // Закрити drawer по кліку зовні
  useEffect(() => {
    if (!drawerOpen && !subSheetId) return;
    const handler = (e) => {
      if (drawerOpen && drawerRef.current && !drawerRef.current.contains(e.target)) {
        setDrawerOpen(false);
        setExpandedInDrawer(null);
      }
      if (subSheetId && subSheetRef.current && !subSheetRef.current.contains(e.target)) {
        setSubSheetId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [drawerOpen, subSheetId]);

  // ── Активність ────────────────────────────────────────────────────────────

  const isBarItemActive = (item) => {
    if (activeItem === item.id) return true;
    return (level2Items[item.id] || []).some(s => s.id === activeItem);
  };

  const isMenuBtnActive = () =>
    drawerItems.some(i => {
      if (i.id === activeItem) return true;
      return (level2Items[i.id] || []).some(s => s.id === activeItem);
    });

  // ── Обробники ─────────────────────────────────────────────────────────────

  const handleBarClick = (item) => {
    // Закрити повний drawer якщо відкритий
    setDrawerOpen(false);
    setExpandedInDrawer(null);

    if (level2Items[item.id]) {
      // Є підменю — тоглимо міні-sheet з підпунктами
      setSubSheetId(prev => prev === item.id ? null : item.id);
      // Також повідомляємо батька про вибір пункту (без навігації — тільки відкрити підменю)
    } else {
      setSubSheetId(null);
      onMenuItemClick && onMenuItemClick(item.id);
    }
  };

  const handleSubSheetItem = (id) => {
    setSubSheetId(null);
    onMenuItemClick && onMenuItemClick(id);
  };

  const handleDrawerItem = (item) => {
    if (level2Items[item.id]) {
      setExpandedInDrawer(prev => prev === item.id ? null : item.id);
      // Якщо пункт має сторінку — переходимо, але залишаємо drawer відкритим
      // для вибору підпункту
    } else {
      setDrawerOpen(false);
      setExpandedInDrawer(null);
      onMenuItemClick && onMenuItemClick(item.id);
    }
  };

  const handleDrawerLevel2 = (id) => {
    setDrawerOpen(false);
    setExpandedInDrawer(null);
    onMenuItemClick && onMenuItemClick(id);
  };

  const handleAddGroup = () => {
    setDrawerOpen(false);
    setSubSheetId(null);
    onAddGroup && onAddGroup();
  };

  const closeAll = () => {
    setDrawerOpen(false);
    setSubSheetId(null);
    setExpandedInDrawer(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const subSheetItem = subSheetId ? menuItems.find(i => i.id === subSheetId) : null;
  const subSheetSubs = subSheetId ? (level2Items[subSheetId] || []) : [];

  return (
    <>
      {/* Overlay — за обома sheet-ами */}
      {(drawerOpen || subSheetId) && (
        <div className="bnz-overlay" onClick={closeAll} />
      )}

      {/* ── Міні-sheet для підпунктів bar-пункту ── */}
      <div
        className={`bnz-subsheet ${subSheetId ? 'bnz-subsheet--open' : ''}`}
        ref={subSheetRef}
        role="dialog"
        aria-label={subSheetItem?.label || 'Підменю'}
      >
        <div className="bnz-drawer__handle" />
        {subSheetItem && (
          <p className="bnz-subsheet__title">{subSheetItem.label}</p>
        )}
        <div className="bnz-drawer__scroll" style={{ paddingTop: 0 }}>
          {subSheetSubs.map(sub => (
            <button
              key={sub.id}
              className={`bnz-drawer__item ${activeItem === sub.id ? 'bnz-drawer__item--active' : ''}`}
              onClick={() => handleSubSheetItem(sub.id)}
            >
              {sub.icon && <img src={sub.icon} alt="" className="bnz-drawer__icon" />}
              <span className="bnz-drawer__label">{sub.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Повний drawer (кнопка "Меню") ── */}
      <div
        className={`bnz-drawer ${drawerOpen ? 'bnz-drawer--open' : ''}`}
        ref={drawerRef}
        role="dialog"
        aria-label="Повне меню"
      >
        <div className="bnz-drawer__handle" />

        {rightPossible?.isPossibleAddGroup && title && (
          <button className="bnz-drawer__add-btn" onClick={handleAddGroup}>
            <span className="bnz-drawer__add-plus">+</span> {title}
          </button>
        )}

        <div className="bnz-drawer__scroll">
          {drawerItems.map(item => {
            const hasSubs  = !!level2Items[item.id];
            const expanded = expandedInDrawer === item.id;
            const isActive = item.id === activeItem ||
              (level2Items[item.id] || []).some(s => s.id === activeItem);
            const badge    = badges[item.id];

            return (
              <div key={item.id} className="bnz-drawer__group">
                <button
                  className={`bnz-drawer__item ${isActive ? 'bnz-drawer__item--active' : ''}`}
                  onClick={() => handleDrawerItem(item)}
                >
                  {item.icon && <img src={item.icon} alt="" className="bnz-drawer__icon" />}
                  <span className="bnz-drawer__label">{item.label}</span>
                  {badge && <span className="bnz-drawer__badge">{badge}</span>}
                  {hasSubs && (
                    <span className={`bnz-drawer__chevron ${expanded ? 'bnz-drawer__chevron--up' : ''}`}>›</span>
                  )}
                </button>

                {hasSubs && expanded && (
                  <div className="bnz-drawer__sub">
                    {level2Items[item.id].map(sub => (
                      <button
                        key={sub.id}
                        className={`bnz-drawer__sub-item ${activeItem === sub.id ? 'bnz-drawer__sub-item--active' : ''}`}
                        onClick={() => handleDrawerLevel2(sub.id)}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {additionalLinks.length > 0 && <div className="bnz-drawer__divider" />}

          {additionalLinks.map((link, idx) => (
            <a
              key={`${link.id}-${idx}`}
              href={link.href}
              className="bnz-drawer__item bnz-drawer__item--link"
              onClick={() => setDrawerOpen(false)}
            >
              {link.icon && <img src={link.icon} alt="" className="bnz-drawer__icon" />}
              <span className="bnz-drawer__label">{link.label}</span>
              <span className="bnz-drawer__ext">↗</span>
            </a>
          ))}
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <nav className="bnz-bar" aria-label="Мобільна навігація">
        {barItems.map(item => {
          const isActive = isBarItemActive(item);
          const badge    = badges[item.id];
          const hasSheet = !!level2Items[item.id];
          const sheetOpen = subSheetId === item.id;

          return (
            <button
              key={item.id}
              className={`bnz-bar__item ${(isActive || sheetOpen) ? 'bnz-bar__item--active' : ''}`}
              onClick={() => handleBarClick(item)}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
              aria-expanded={hasSheet ? sheetOpen : undefined}
            >
              <span className="bnz-bar__icon-wrap">
                {item.icon && (
                  <img
                    src={item.icon}
                    alt=""
                    className="bnz-bar__icon"
                    style={(isActive || sheetOpen)
                      ? { filter: 'invert(27%) sepia(99%) saturate(2000%) hue-rotate(210deg) brightness(100%)' }
                      : { filter: 'invert(55%) sepia(0%) saturate(0%) brightness(85%)' }
                    }
                  />
                )}
                {badge > 0 && (
                  <span className="bnz-bar__badge">{badge > 99 ? '99+' : badge}</span>
                )}
              </span>
              <span className="bnz-bar__label">{item.label}</span>
            </button>
          );
        })}

        {/* Кнопка "Меню" */}
        <button
          className={`bnz-bar__item ${(drawerOpen || isMenuBtnActive()) ? 'bnz-bar__item--active' : ''}`}
          onClick={() => { setSubSheetId(null); setDrawerOpen(prev => !prev); }}
          aria-label="Меню"
          aria-expanded={drawerOpen}
        >
          <span className="bnz-bar__icon-wrap">
            <span className="bnz-bar__burger" aria-hidden="true">
              <span /><span /><span />
            </span>
          </span>
          <span className="bnz-bar__label">Меню</span>
        </button>
      </nav>
    </>
  );
};

export default BottomNavZdo;